/**
 * Upload domain API (Spec 059, E2). Moved bitgleich from the former monolithic `api.ts` — no
 * behavior change (E5).
 */
import type { ApiCore } from './core';

export interface UploadBatchDocumentClassification {
  category: string;
  subCategory: string;
  confidence: number;
  isManualClassified?: boolean;
}

export interface UploadedDocument {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: string;
  uploadedAt: string;
  classification: UploadBatchDocumentClassification | null;
  extractedData: Record<string, string> | null;
  assignedTo: string[];
  userTags: string[];
  householdId: string | null;
  notes?: string;
}

export interface UploadResponse {
  uploadBatchId: string;
  documents: UploadedDocument[];
}

/** A file that could not be uploaded by {@link UploadApi.uploadDocumentsChunked} (#279, #298). */
export interface SkippedFile {
  fileName: string;
  /**
   * Stable reason key (UI renders localized text):
   * - `too_large` — file exceeds the policy upload ceiling (50 MB, spec 004 E2)
   * - `failed` — the chunk request or direct upload step failed
   */
  reason: 'too_large' | 'failed';
  /**
   * Optional human-readable message, present when `reason === 'too_large'` due to policy limit.
   * References the real limit (50 MB) so the UI can distinguish from the 24 MiB chunk limit.
   */
  message?: string;
}

export interface ChunkedUploadResult {
  response: UploadResponse;
  skipped: SkippedFile[];
}

/**
 * Per-request upload limit for chunked multipart uploads (#279). Cloud Run caps ingress at 32 MiB;
 * 24 MiB leaves headroom for multipart boundaries and the extra form fields.
 */
const CHUNK_MAX_BYTES = 24 * 1024 * 1024;
/** Per-request file-count cap, aligned with the backend MaxBatchSize guard. */
const CHUNK_MAX_FILES = 50;
/**
 * Policy upload ceiling (50 MB, spec 004 E2).
 * Mirrors FileValidationGuard.MaxFileSizeBytes on the backend.
 * Files above this limit are rejected client-side (and server-side) with the real limit stated.
 * Files between CHUNK_MAX_BYTES and MAX_FILE_BYTES use the pre-signed direct-upload path (spec 004, E7).
 */
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

export interface UploadBatchStatus {
  uploadBatchId: string;
  totalDocuments: number;
  processedDocuments: number;
  failedDocuments: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  documents: {
    id: string;
    fileName: string;
    status: string;
    classification: UploadBatchDocumentClassification | null;
    extractedData: Record<string, string> | null;
    assignedTo?: string[];
    userTags?: string[];
    householdId?: string;
  }[];
}

export class UploadApi {
  constructor(private readonly core: ApiCore) {}

  /**
   * Upload documents (single request).
   * Pass `uploadBatchId` to merge this request into an existing batch (chunked upload, #279).
   */
  async uploadDocuments(
    files: File[],
    metadata?: {
      systemTags?: string[];
      assignedTo?: string[];
      householdId?: string;
      uploadBatchId?: string;
    },
    opts?: { suppressGlobalErrorHandler?: boolean },
  ): Promise<UploadResponse> {
    const formData = new FormData();

    files.forEach((file) => {
      formData.append('files', file);
    });

    if (metadata) {
      if (metadata.systemTags) formData.append('tags', metadata.systemTags.join(','));
      if (metadata.assignedTo) formData.append('assignedTo', metadata.assignedTo.join(','));
      if (metadata.householdId) formData.append('householdId', metadata.householdId);
      if (metadata.uploadBatchId) formData.append('uploadBatchId', metadata.uploadBatchId);
    }

    return this.core.fetchJson<UploadResponse>(
      `${this.core.baseUrl}/api/v1/documents`,
      {
        method: 'POST',
        headers: this.core.getAuthHeaders(),
        body: formData,
      },
      opts?.suppressGlobalErrorHandler ?? false,
    );
  }

  /**
   * Upload documents, splitting them client-side into size-bounded chunks (#279).
   * Spec 004: a single large file (CHUNK_MAX_BYTES < size ≤ MAX_FILE_BYTES) uses
   * the pre-signed direct-upload path instead of multipart chunking.
   *
   * Routing (spec 004, E7):
   * - size > MAX_FILE_BYTES → skipped (reason `too_large`, message references 50 MB)
   * - single file, CHUNK_MAX_BYTES < size ≤ MAX_FILE_BYTES → direct upload (upload-url → PUT to GCS → finalize)
   * - otherwise → multipart chunking as in #279 (unchanged regression path)
   *
   * @returns the merged response across all successful uploads plus the list of skipped files.
   */
  async uploadDocumentsChunked(
    files: File[],
    metadata?: { systemTags?: string[]; assignedTo?: string[]; householdId?: string },
    onProgress?: (chunkIndex: number, chunkCount: number) => void,
  ): Promise<ChunkedUploadResult> {
    const uploadBatchId = crypto.randomUUID();
    const skipped: SkippedFile[] = [];

    // Routing (spec 004, E7):
    //   1. size > MAX_FILE_BYTES (50 MB) → too_large with policy message (spec E2)
    //   2. single file, CHUNK_MAX_BYTES < size ≤ MAX_FILE_BYTES → direct upload (spec S7)
    //   3. CHUNK_MAX_BYTES < size AND multiple files → too_large (can't fit in multipart)
    //   4. size ≤ CHUNK_MAX_BYTES → multipart chunk (spec S9, unchanged from #279)
    const uploadable: File[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_BYTES) {
        // Policy ceiling exceeded — client and server both reject this (spec 004, E2)
        skipped.push({
          fileName: file.name,
          reason: 'too_large',
          message: 'File exceeds the 50 MB upload policy limit.',
        });
      } else if (file.size > CHUNK_MAX_BYTES && files.length > 1) {
        // Large file in a multi-file batch: can't fit in any multipart chunk (legacy behavior)
        skipped.push({ fileName: file.name, reason: 'too_large' });
      } else {
        uploadable.push(file);
      }
    }

    const documents: UploadedDocument[] = [];

    // Spec 004, E7 / S7: single file in the direct-upload range → use pre-signed PUT URL
    if (uploadable.length === 1 && uploadable[0].size > CHUNK_MAX_BYTES) {
      const file = uploadable[0];
      const directUploadResult = await this.directUploadSingleFile(file, metadata, uploadBatchId);
      if (directUploadResult) {
        documents.push(directUploadResult);
      } else {
        skipped.push({ fileName: file.name, reason: 'failed' });
      }
      return { response: { uploadBatchId, documents }, skipped };
    }

    // Spec 004, S9 (regression): multipart chunking for small files and multi-file batches (#279)
    // Greedy bin-packing under the size and count limits.
    const chunks: File[][] = [];
    let current: File[] = [];
    let currentBytes = 0;
    for (const file of uploadable) {
      const wouldExceed =
        currentBytes + file.size > CHUNK_MAX_BYTES || current.length >= CHUNK_MAX_FILES;
      if (current.length > 0 && wouldExceed) {
        chunks.push(current);
        current = [];
        currentBytes = 0;
      }
      current.push(file);
      currentBytes += file.size;
    }
    if (current.length > 0) chunks.push(current);

    for (let i = 0; i < chunks.length; i++) {
      onProgress?.(i + 1, chunks.length);
      try {
        // Suppress the global error toast for chunk failures — the partial-failure summary is
        // the single user-facing signal (#279 Finding A). 401 still redirects via handleApiError.
        const res = await this.uploadDocuments(
          chunks[i],
          { ...metadata, uploadBatchId },
          { suppressGlobalErrorHandler: true },
        );
        documents.push(...res.documents);
      } catch {
        // Stable reason key (not the raw error message) so the UI can render localized text (#279 Finding F).
        for (const file of chunks[i]) {
          skipped.push({ fileName: file.name, reason: 'failed' });
        }
      }
    }

    return { response: { uploadBatchId, documents }, skipped };
  }

  /**
   * Direct upload path for a single large file (spec 004, S7 / E3):
   * 1. POST /api/v1/documents/upload-url → get signed PUT URL + documentId
   * 2. PUT to the GCS pre-signed URL with the raw file bytes
   * 3. POST /api/v1/documents/{id}/finalize → create the Document aggregate
   *
   * Returns the uploaded document on success, or null on any step failure.
   */
  private async directUploadSingleFile(
    file: File,
    metadata: { systemTags?: string[]; assignedTo?: string[]; householdId?: string } | undefined,
    uploadBatchId: string,
  ): Promise<UploadedDocument | null> {
    // Step 1: Request a signed PUT URL from the backend
    let uploadUrlResponse: {
      documentId: string;
      objectKey: string;
      uploadUrl: string;
      expiresInSeconds: number;
    };
    try {
      const urlRes = await fetch(`${this.core.baseUrl}/api/v1/documents/upload-url`, {
        method: 'POST',
        headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
          fileSize: file.size,
          uploadBatchId,
        }),
      });
      if (!urlRes.ok) {
        return null;
      }
      uploadUrlResponse = await urlRes.json();
    } catch {
      return null;
    }

    // Step 2: PUT the file bytes directly to GCS (no auth header — the signed URL is self-authorizing)
    try {
      const putRes = await fetch(uploadUrlResponse.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) {
        return null;
      }
    } catch {
      return null;
    }

    // Step 3: Finalize — tell the backend to verify the object and create the Document aggregate.
    // Pass objectKey from upload-url response so the backend resolves the GCS object without
    // date reconstruction (avoids midnight-boundary race, spec 004 Finding B).
    // objectKey is the relative path (e.g. documents/2026/06/{id}.pdf) without gs:// prefix (#231-compliant).
    try {
      const finalizeRes = await fetch(
        `${this.core.baseUrl}/api/v1/documents/${uploadUrlResponse.documentId}/finalize`,
        {
          method: 'POST',
          headers: { ...this.core.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            objectKey: uploadUrlResponse.objectKey,
            householdId: metadata?.householdId ?? null,
            tags: metadata?.systemTags ?? null,
            assignedTo: metadata?.assignedTo ?? null,
            uploadBatchId,
          }),
        },
      );
      if (!finalizeRes.ok) {
        return null;
      }

      const finalizeBody = await finalizeRes.json();
      // Map finalize response to UploadedDocument shape
      return {
        id: finalizeBody.documentId ?? uploadUrlResponse.documentId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type,
        status: 'uploaded',
        uploadedAt: new Date().toISOString(),
        classification: null,
        extractedData: null,
        assignedTo: metadata?.assignedTo ?? [],
        userTags: [],
        householdId: metadata?.householdId ?? null,
      };
    } catch {
      return null;
    }
  }

  /**
   * Get upload batch status
   */
  async getUploadBatchStatus(uploadBatchId: string): Promise<UploadBatchStatus> {
    return this.core.fetchJson<UploadBatchStatus>(
      `${this.core.baseUrl}/api/v1/upload/${uploadBatchId}/status`,
      {
        headers: this.core.getAuthHeaders(),
      },
    );
  }
}
